<template>
  <div class="pa-4 pt-8">
    <!-- Add Note Form -->
      <v-card v-if="showAddNote" class="mb-4 pa-4" variant="outlined">
        <v-textarea
          v-model="newNote"
          label="Add a note"
          variant="outlined"
          density="compact"
          rows="3"
          hide-details
        ></v-textarea>
        <div class="mt-3 d-flex justify-end">
          <v-btn
            variant="text"
            @click="cancelAddNote"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="tonal"
            class="ml-2"
            :loading="addingNote"
            @click="addNote"
          >
            Save Note
          </v-btn>
        </div>
      </v-card>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <div v-if="!loading && notes.length === 0 && !showAddNote" class="text-center pa-8">
        <v-icon size="48" color="grey">mdi-note-text-outline</v-icon>
        <div class="text-body-1 mt-2">No notes yet</div>
        <div class="text-caption text-grey mt-1">Click "Add Note" to create the first note</div>
      </div>

      <!-- Notes List -->
      <div v-if="!loading && notes.length > 0" class="notes-list">
        <v-card
          v-for="note in notes"
          :key="note.id"
          variant="outlined"
          class="mb-2"
        >
          <v-card-text class="py-2 px-3 d-flex align-start">
            <div class="note-date text-caption text-grey">
              {{ formatNoteDate(note.created_at) }}
            </div>
            <div class="note-content flex-grow-1">
              {{ note.client_note }}
            </div>
          </v-card-text>
        </v-card>
      </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { format } from "date-fns";
import axios from "axios";

const props = defineProps({
  client: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["notes-updated"]);

const showAddNote = ref(false);
const newNote = ref("");
const addingNote = ref(false);

const formatNoteDate = (dateString) => {
  try {
    return format(new Date(dateString), "PPp");
  } catch (e) {
    return dateString;
  }
};

const cancelAddNote = () => {
  showAddNote.value = false;
  newNote.value = "";
};

const triggerAddNote = () => {
  showAddNote.value = true;
};

// Expose method to parent component
defineExpose({
  triggerAddNote,
});

const addNote = async () => {
  if (!newNote.value.trim()) return;

  addingNote.value = true;
  const apiURL = import.meta.env.VITE_API_URL;

  try {
    await axios.post(apiURL + "notes", {
      customer: {
        firstname: props.client.firstname,
        lastname: props.client.lastname,
        email: props.client.email,
        phone: props.client.phone,
      },
      salon_id: getPageTitle(),
      salon_client_id: props.client.id,
      salon_appointment_id: 0,
      salon_appointment_service_id: [],
      client_note: newNote.value,
    });

    newNote.value = "";
    showAddNote.value = false;
    emit("notes-updated");
  } catch (error) {
    console.error("Error adding note:", error);
    alert("Failed to add note. Please try again.");
  } finally {
    addingNote.value = false;
  }
};

const getPageTitle = () => {
  let indi = document.title.search("([0-9]|[1-9])");
  return document.title
    .slice(indi + 2)
    .replaceAll("_", " ")
    .trim();
};
</script>

<style scoped>
.notes-list {
  max-width: 100%;
}

.note-date {
  min-width: 160px;
  max-width: 160px;
  flex-shrink: 0;
  padding-right: 16px;
}

.note-content {
  line-height: 1.4;
}
</style>
